import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Col, Collapse, Divider, Modal, Row, Skeleton, Table, Tooltip, Tour } from "antd";
import API from "API";
import { considerCourseStatus, courseStatusComponent, formatVND } from "general_function";
import dayjs from "dayjs";
import TermHandle from "components/pages/UniversitySide/UniversityTerms/TermHandle";
import { CheckCircleFilled, CheckSquareFilled, CloseSquareFilled } from "@ant-design/icons";
import { theme } from 'antd';
import "./style.css";
import { useMask } from "App";
import Orders from "./Orders";
import PayPopup from "./PayPopup";
const { useToken } = theme;

function checkRequiredCourses(requiredCourses) {
    for (const c of requiredCourses) {
        if (c.status !== 3) {
            return false;
        }
    }
    return true;
}

const NOW = dayjs();

const CourseColumns = [
    { "title": "Mã môn", "key": "code", "dataIndex": "code" },
    { "title": "Môn học", "key": "name", "dataIndex": "name" },
    { "title": "Tín chỉ", "key": "credit", "dataIndex": "credit" },
    { "title": "Bắt buộc", "key": "mandatory", "dataIndex": "mandatory", render: v => v ? <span style={{ color: "green" }}>
        <CheckSquareFilled />
    </span> : <span style={{ color: "red" }}>
        <CloseSquareFilled />
    </span> },
    { "title": "Trạng thái", "key": "status", "dataIndex": "status", render: v => courseStatusComponent[v] },
    {
        "title": "Môn tiên quyết", "key": "required_courses", "dataIndex": "required_courses", render: v => <>
            {v.map(course => <span key={course.id} style={{
                color: "blue", cursor: "pointer"
            }} onClick={() => {
                const rowEl = document.querySelector(`.course-${course.id}`);
                rowEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }}>{course.name}<br /></span>)}
        </>
    },
]

const StudentEnrolment = () => {
    const [courses, setCourses] = useState([]);
    const [majors, setMajors] = useState([]);
    const [selectedMajor, setSelectedMajor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [gettingTerm, setGettingTerm] = useState(false);
    const [gettingCourses, setGettingCourses] = useState(true);
    const [terms, setTerms] = useState(null);
    const [selectedTerm, setSelecterdTerm] = useState(null);
    const [enrollCourses, setEnrollCourses] = useState([]);
    const [gettingCurrentPrice, setGettingCurrentPrice] = useState(false);
    const [currentPrice, setCurrentPrice] = useState(null);
    const [openTour, setOpenTour] = useState(false);
    const [openPayModel, setOpenPayModel] = useState(false);
    const [orders, setOrders] = useState(null);
    const [checkoutURL, setCheckoutURL] = useState(null);
    const [payingOrder, setPayingOrder] = useState(null);
    const cartRef = useRef(null);
    const { setFullMask } = useMask();
    const { token } = useToken();

    const steps = useMemo(() => [
        {
            title: 'Các môn đăng ký',
            description: <>Tổng số tín chỉ và học phí tại đây.<br />
                Các bạn hãy nhấn vào đây để tiến hành thanh toán nhé!</>,
            target: () => cartRef.current,
        }
    ], [])

    const getCourses = useCallback(async (selectedMajor) => {
        setGettingCourses(true);
        try {
            const response = await API.post('student/enroll-course/', { major_id: selectedMajor.id });
            for (const c of response.data) {
                for (var index = 0; index < c.required_courses.length; index++) {
                    // eslint-disable-next-line
                    c.required_courses[index] = response.data.find(item => item.id === c.required_courses[index]);
                }
                for (const s of c.studentcourse) {
                    s.start_time = dayjs(s.start_time);
                    s.end_time = dayjs(s.end_time);
                }
                c.status = considerCourseStatus(c.studentcourse);
            }
            setCourses(response.data.sort((a, b) => a.status - b.status));
        } catch (e) {
            console.log(e);
        } finally {
            setGettingCourses(false);
        }
    }, [])

    useEffect(() => {
        async function getData() {
            setLoading(true);
            try {
                const response = await API.get('student/majors/');
                const results = response.data.results;
                setMajors(results);
                if (results.length > 0) {
                    setSelectedMajor(results[0]);
                }
            } catch (e) {
                console.log(e);
            } finally {
                setLoading(false);
            }
        }
        getData();
    }, [])

    useEffect(() => {
        if (selectedMajor === null) return;
        getCourses(selectedMajor);
    }, [getCourses, selectedMajor])

    useEffect(() => {
        async function getTermData() {
            try {
                setGettingTerm(true);
                const response = await API.get("/student/terms/", {
                    params: {
                        page_size: 1000
                    }
                });
                var check = false;
                for (const r of response.data.results) {
                    r.start_time = dayjs(r.start_time);
                    r.end_time = dayjs(r.end_time);
                    if (r.register_start_time) r.register_start_time = dayjs(r.register_start_time);
                    if (r.register_end_time) r.register_end_time = dayjs(r.register_end_time);
                    if (!check) {
                        if (r.start_time.isBefore(NOW) && r.end_time.isAfter(NOW)) {
                            check = true;
                            setSelecterdTerm(r);
                        }
                    }
                }
                if (!check) {
                    setSelecterdTerm(response.data.results[0] || null);
                }
                setTerms(response.data.results);
                setGettingTerm(false);
            } catch (e) {
                console.log(e);
            } finally {

            }
        }
        getTermData();
    }, [])

    useEffect(() => {
        async function getCurrentPrice() {
            try {
                setGettingCurrentPrice(true);
                const response = await API.get("/get-current-price/");
                setCurrentPrice(response.data || 0);
                setGettingCurrentPrice(false);
            } catch (e) {
                console.log(e);
            } finally {

            }
        }
        getCurrentPrice();
    }, [])

    const allowRegister = useMemo(() => {
        if (!selectedTerm?.register_start_time || !selectedTerm?.register_end_time) {
            return false;
        }
        if (selectedTerm.register_start_time.isBefore(NOW) && selectedTerm.register_end_time.isAfter(NOW)) {
            return true;
        }
        return false;
    }, [selectedTerm])

    const courseColumns = useMemo(() => {
        return [
            ...CourseColumns,
            {
                "title": <></>, "key": "action", render: v => {
                    if (v.status > 1) return <></>;
                    const include = enrollCourses.find(item => item.id === v.id);
                    const check = allowRegister && checkRequiredCourses(v.required_courses);
                    return <Tooltip title={!allowRegister ?
                        selectedTerm.register_start_time && selectedTerm.register_end_time
                            ? "Chưa đến thời gian đăng ký môn"
                            : "Kỳ này không được phép đăng ký môn"
                        : !check && "Chưa hoàn thành môn tiên quyết"}>
                        <Button type="primary" style={{ width: 130, backgroundColor: include ? "green" : "" }} disabled={!check} onClick={() => {
                            if (!localStorage.getItem("tour-enrollment")) {
                                setOpenTour(true);
                                localStorage.setItem("tour-enrollment", "y");
                            }
                            if (!include) {
                                setEnrollCourses([...enrollCourses, v]);
                            } else {
                                setEnrollCourses(enrollCourses.filter(item => item.id !== v.id));
                            }
                        }}>
                            {include ? <span>Đã đăng ký <CheckCircleFilled /></span> : "Đăng ký học"}
                        </Button>
                    </Tooltip>
                }
            }
        ]
    }, [allowRegister, enrollCourses, selectedTerm])

    const enrollCoursesInformation = useMemo(() => {
        var tuition = 0;
        var credit = 0;
        for (const c of enrollCourses) {
            tuition += c.credit * currentPrice;
            credit += c.credit;
        }
        return {
            tuition: tuition,
            credit: credit,
        }
    }, [enrollCourses, currentPrice])

    const handleEnroll = useCallback(async () => {
        setFullMask(true);
        try {
            const response = await API.post("/student/get-payment-link/", { courses: enrollCourses.map(item=>item.id), term_id: selectedTerm.id })
            setOrders(orders=>{
                if (orders === null) return orders;
                return [response.data.order, ...orders];
            })
            setOpenPayModel(false);
            setCheckoutURL(response.data.checkout_url);
            setPayingOrder(response.data.order);
            setEnrollCourses([]);
        } catch {

        } finally {
            setFullMask(false);
        }
    }, [enrollCourses, selectedTerm, setFullMask])

    const creditInfo = useMemo(() => {
        const result = {
            passed_mandatory_credits: 0,
            passed_option_credits: 0,
            studying_credits: 0,
        }
        if (!selectedMajor) return result;
        var mandatoryCredit = 0;
        for (const course of courses) {
            if (course.mandatory) mandatoryCredit += course.credit;
            if (course.status === 2) {
                result.studying_credits += course.credit;
            } else if (course.status === 3) {
                if (course.mandatory) {
                    result.passed_mandatory_credits += course.credit;
                } else {
                    result.passed_option_credits += course.credit;
                }
            }
        }
        result.passed_option_credits = Math.min(result.passed_option_credits, selectedMajor.credit - mandatoryCredit);
        return result;
    },[courses, selectedMajor])

    return loading || gettingTerm || gettingCurrentPrice ? <Skeleton active /> : <>
        <Collapse
            items={[{ key: "1", label: "Thanh toán", children: <Orders orders={orders} setOrders={setOrders}
                setCheckoutURL={setCheckoutURL} setPayingOrder={setPayingOrder} payingOrder={payingOrder}/> }]}
        />
        <div>
            {majors.length === 0 && <>Bạn chưa được đăng ký vào bất kỳ ngành nào</>}
            {majors.map(major => <div key={major.id} className={`term-div ${major.id === selectedMajor?.id ? "selected" : ""}`}
                onClick={() => selectedMajor(major)}
            >
                {major.name}
            </div>)}
        </div>
        <TermHandle terms={terms} setTerms={setTerms} edit={false}
            selectedTerm={selectedTerm} setSelecterdTerm={setSelecterdTerm} />
        <Row>
            <Col lg={12} md={24} sm={24}>
                <b>Số tin chỉ đã tích luỹ:</b> { creditInfo.passed_mandatory_credits + creditInfo.passed_option_credits }/{selectedMajor?.credit} <br/>
                <b>Bắt buộc:</ b> { creditInfo.passed_mandatory_credits } | <b>Tự chọn:</b> { creditInfo.passed_option_credits }
            </Col>
             <Col lg={12} md={24} sm={24}>
                <b>Số tin chỉ đang học:</b> {creditInfo.studying_credits}/{selectedMajor?.credit}
            </Col>
        </Row>
        <div>
            {!selectedTerm ? "Vui lòng chọn kỳ" :
                allowRegister ? <b style={{ color: "green" }}>Đang trong thời gian đăng ký môn: {selectedTerm.register_start_time.format("DD/MM/YYYY")} - {selectedTerm.register_end_time.format("DD/MM/YYYY")}</b>
                    : !selectedTerm.register_end_time || !selectedTerm.register_start_time ?
                        <b style={{ color: "red" }}>Kỳ này không được phép đăng ký môn</b>
                        : <b style={{ color: "red" }}>Đang không trong thời gian đăng ký môn: {selectedTerm.register_start_time.format("DD/MM/YYYY")} - {selectedTerm.register_end_time.format("DD/MM/YYYY")}</b>}
        </div>
        <div style={{ overflow: "auto" }}>
            <Table
                rowKey="id"
                loading={gettingCourses}
                columns={courseColumns}
                dataSource={courses}
                rowClassName={(record) => `course-${record.id}`}
                pagination={false}
            />
        </div>
        {allowRegister && <Tooltip>
            <div ref={cartRef} style={{
                position: "fixed",
                left: 5, bottom: 5,
                padding: 5, width: 100,
                borderRadius: 5,
                boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
                backgroundColor: token.colorPrimary,
                cursor: "pointer",
            }} onClick={() => enrollCourses.length !== 0 && setOpenPayModel(true)}>
                <Badge count={enrollCourses.length} offset={[10, -5]}>
                    <div style={{ color: "white", width: 85 }}>
                        <b>Tín chỉ:</b> {enrollCoursesInformation.credit}
                        <Divider style={{ margin: "5px 0px", borderColor: "white" }} />
                        <b>{formatVND(enrollCoursesInformation.tuition)} ₫</b>
                    </div>
                </Badge>
            </div>
        </Tooltip>}
        <Tour open={openTour} onClose={() => setOpenTour(false)} steps={steps} />
        <Modal
            open={openPayModel}
            onCancel={() => setOpenPayModel(false)}
            width={1000}
            footer={[
                <Button key="cancel" onClick={() => setOpenPayModel(false)}>
                    Đóng
                </Button>,
                <Button type="primary" key="confirm" onClick={handleEnroll}>
                    Thanh toán
                </Button>
            ]}
        >
            <div style={{ overflow: "auto" }}>
                <Table
                    rowClassName={(_, index) => {
                        if (index === enrollCourses.length) {
                            return "highlight-row";
                        }
                    }}
                    columns={[
                        {
                            "title": "Mã môn", "key": "code", "dataIndex": "code", onCell: (_, index) => {
                                if (index === enrollCourses.length) {
                                    return { colSpan: 2 };
                                }
                            }
                        },
                        {
                            "title": "Môn học", "key": "name", "dataIndex": "name", onCell: (_, index) => {
                                if (index === enrollCourses.length) {
                                    return { colSpan: 0 };
                                }
                            }
                        },
                        { "title": "Tín chỉ", "key": "credit", "dataIndex": "credit" },
                        {
                            "title": "Học phí", "key": "tuition", "dataIndex": "credit", render: value => <>
                                {formatVND(value * currentPrice)}  ₫
                            </>
                        },
                    ]}
                    pagination={false}
                    dataSource={[
                        ...enrollCourses,
                        { "code": <b>Tổng:</b>, "credit": enrollCoursesInformation.credit }
                    ]}
                />
            </div>
        </Modal>
        <PayPopup checkoutUrl={checkoutURL} setCheckoutUrl={setCheckoutURL} setOrders={setOrders} setPayingOrder={setPayingOrder} payingOrder={payingOrder}/>
    </>
};

export default StudentEnrolment;
